import { useEffect, useRef, useState } from 'react';

import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { camelCase, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { getUsersEmployees } from 'RestAPIs/employees';

import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { FontAwesomeSvgIcon } from 'components/shared';

import EmployeeSelector, {
  Employee,
  EmployeeSelectorHandle
} from './EmployeeSelector';

const PreferredEmployeesStyles = styled.div`
  .employee-selector {
    margin-bottom: 15px;
    padding: 0 16px;
  }
  .employees-table {
    width: 100%;
    table-layout: fixed;
    margin-bottom: 15px;
  }
`;

export type CreatedBy = 'EMPLOYEE' | 'PLANNER' | 'FLEX_PLANNER';

export type PreferredEmployee = {
  createdBy: CreatedBy;
} & Employee;

type Props = {
  date: Date;
  employees?: PreferredEmployee[];
  disablePortal?: true;
  readOnly?: boolean;
  createdBy: CreatedBy;
  onlyFlex: boolean;
  showProposed?: boolean;
  addEmployee?: (employee: Employee) => Promise<void>;
  removeEmployee?: (employee: Employee) => Promise<void>;
};

export default function PreferredEmployees(props: Props) {
  const {
    date,
    employees,
    disablePortal,
    readOnly,
    createdBy,
    onlyFlex,
    showProposed,
    addEmployee,
    removeEmployee
  } = props;
  const { t } = useTranslation('common');
  const [preferredEmployees, setPreferredEmployees] = useState<
    PreferredEmployee[]
  >(employees || []);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const employeeSelectorRef = useRef<EmployeeSelectorHandle>();

  const onEmployeeSelected = (options: Employee[]) => {
    if (preferredEmployees.length < options.length) {
      const employee = preferredEmployees.length
        ? options.filter(
            ({ employeeId }) =>
              !preferredEmployees
                .map(({ employeeId }) => employeeId)
                .includes(employeeId)
          )[0]
        : options[0];

      if (addEmployee) {
        addEmployee(employee).then(
          () => {
            setPreferredEmployees([
              ...preferredEmployees,
              { ...employee, createdBy }
            ]);
          },
          () => {
            employeeSelectorRef?.current?.uncheckEmployee(employee, false);
          }
        );
      }
    } else {
      const employee = preferredEmployees.filter(
        ({ employeeId }) =>
          !options.map(({ employeeId }) => employeeId).includes(employeeId)
      )[0];

      if (removeEmployee) {
        removeEmployee(employee).then(
          () => {
            setPreferredEmployees(
              preferredEmployees.filter(
                ({ employeeId }) => employeeId !== employee.employeeId
              )
            );
          },
          () => {
            employeeSelectorRef?.current?.checkEmployee(employee, false);
          }
        );
      }
    }
  };

  useEffect(() => {
    if (employees && allEmployees.length) {
      const mappedEmployees = employees
        .map(({ employeeId, createdBy }) => {
          const employee = allEmployees.find(
            (e) => e.employeeId === employeeId
          );
          if (!employee) {
            return null;
          }
          return { ...employee, createdBy };
        })
        .flatMap((e) => (e ? [e] : []));
      employeeSelectorRef?.current?.setSelectedEmployees(mappedEmployees);
      setPreferredEmployees(mappedEmployees);
    }
  }, [employees, allEmployees]);

  useEffect(() => {
    if (date) {
      getUsersEmployees(date, onlyFlex).then(({ data }) => {
        setAllEmployees(
          data.map(({ empId, nameInformal, nr }) => ({
            employeeId: empId,
            employeeName: nameInformal,
            employeeNr: nr,
            id: empId,
            nr,
            label: `${nr} - ${nameInformal}`
          }))
        );
      });
    }
  }, [date, createdBy, onlyFlex]);

  return (
    <PreferredEmployeesStyles className="preferred-employees">
      {!readOnly && (
        <EmployeeSelector
          ref={employeeSelectorRef}
          options={allEmployees}
          isMulti
          disablePortal={disablePortal}
          onSelected={onEmployeeSelected}
        />
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left" width="90px">
              <strong>{t('employeeNr')}.</strong>
            </TableCell>
            <TableCell align="left" width="50%">
              <strong>{t('employeeName')}</strong>
            </TableCell>
            {!showProposed && createdBy && (
              <TableCell align="center" width="100px">
                <strong>{t('employeeType')}</strong>
              </TableCell>
            )}
            {!readOnly && <TableCell align="center" width="32px" />}
          </TableRow>
        </TableHead>
        {(readOnly ? employees || [] : preferredEmployees).length > 0 && (
          <TableBody>
            {sortBy(
              readOnly ? employees : preferredEmployees,
              'employeeName'
            ).map((employee) => {
              const { employeeId, employeeName, employeeNr, createdBy } =
                employee;
              return (
                <TableRow
                  key={employeeId}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell align="left" width="90px">
                    {employeeNr}
                  </TableCell>
                  <TableCell align="left" width="50%">
                    {employeeName}
                  </TableCell>
                  {!showProposed && createdBy && (
                    <TableCell align="center" width="100px">
                      {t(camelCase(createdBy))}
                    </TableCell>
                  )}
                  {!readOnly && (
                    <TableCell align="center" width="32px">
                      <IconButton
                        onClick={() =>
                          employeeSelectorRef?.current?.uncheckEmployee(
                            employee
                          )
                        }
                        size="small"
                      >
                        <FontAwesomeSvgIcon icon={faTimes} fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
    </PreferredEmployeesStyles>
  );
}
